import { hasRememberedSession } from '@/lib/api'

const KEY = 'kapro.lastRoute'

/*
 * Where the user was when they last used the app.
 *
 * This is the other half of "remember me": staying signed in is only useful if
 * reopening the app puts you back where you were. A supervisor who was working
 * through the open queue should reopen on the open queue, not on the home
 * screen.
 *
 * Deliberately tied to the remember-me choice. It lives in localStorage, which
 * is exactly the store that "do not remember me" avoids — so on a shared
 * terminal nothing is left behind pointing at the previous person's work.
 *
 * **Who clears it, precisely**, because this used to be written down wrongly:
 *
 * - A deliberate sign-out clears it. `AuthContext.logout()` calls
 *   `clearRememberedRoute()` itself — `clearToken()` does *not*, and never did.
 * - Signing in with the box unticked clears it, because that choice means
 *   "leave no trail on this device".
 * - A session **expiring** deliberately leaves it alone. Being sent back to the
 *   screen you were working on is the entire point; erasing the crumb on a 401
 *   would defeat it.
 */

/** Routes that would be wrong or useless to return to. */
function isWorthRemembering(path: string): boolean {
  if (!path.startsWith('/') || path.startsWith('//')) return false

  // The login page itself, and the QR landing route — the latter is a redirect
  // stop that immediately forwards elsewhere, so returning to it would replay a
  // scan the user is no longer standing in front of.
  return !path.startsWith('/prijava') && !path.startsWith('/qr/')
}

export function rememberRoute(path: string): void {
  // Only when the session is a remembered one. sessionStorage-only sessions are
  // the shared-terminal case and must not leave a trail.
  if (!hasRememberedSession()) return
  if (!isWorthRemembering(path)) return

  localStorage.setItem(KEY, path)
}

/** The remembered route, if it is still safe to use. */
export function getRememberedRoute(): string | null {
  const path = localStorage.getItem(KEY)

  return path && isWorthRemembering(path) ? path : null
}

export function clearRememberedRoute(): void {
  localStorage.removeItem(KEY)
}
